import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { useParams } from 'react-router-dom';
import {
  Button,
  Box,
  ClickAwayListener,
  FormControl,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import DatePicker from 'react-datepicker';
import Snackbar from '../Snackbar';
import { getReport } from '../../requests/api/report';
import useStyles from './styles';
import powerbiComment from '../../requests/api/powerbi-comment';

const MENTION_PATTERN = /(^|\s)@([^\s@]*)$/;

const Report = forwardRef(({ fullScreen, onReportLoaded, onEnabledDownloadPdfChange }, ref) => {
  const { t, i18n } = useTranslation();

  const reportRef = useRef(null);
  const [reportId, setReportId] = useState();
  const [url, setUrl] = useState();
  const [embedToken, setEmbedToken] = useState();
  const [filters, setFilters] = useState();
  const [loading, setLoading] = useState(true);
  const [selectedContext, setSelectedContext] = useState(null);
  const [comment, setComment] = useState('');
  const [reportUsers, setReportUsers] = useState([]);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const commentInputRef = useRef(null);
  const { id } = useParams();
  const styles = useStyles();
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('');
  const [commentAllowed, setCommentAllowed] = useState(false);
  const [commentAllowedPages, setCommentAllowedPages] = useState(null);
  const [deadLineDate, setDeadLineDate] = useState(new Date());
  const currentPageRef = useRef(null);
  const shortLocale = i18n.language ? i18n.language.slice(0, 2) : 'es';
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [pageVersion, setPageVersion] = useState(null);

  const updatePage = (value) => {
    currentPageRef.current = value;
    setPageVersion(value);
  };

  const downloadPDF = useCallback(async () => {
    if (!reportRef.current) {
      setSeverity('error');
      setMessage(t('reports.noReportAvailable'));
      return;
    }

    try {
      setSeverity('info');
      setMessage(t('reports.generatingPdf'));

      await reportRef.current.print();

      setSeverity('success');
      setMessage(t('reports.pdfGenerated'));
    } catch (error) {
      setSeverity('error');

      const errorMessage = error instanceof Error ? error.message : t('reports.pdfDownloadError');

      setMessage(errorMessage);
    }
  }, [t]);

  useEffect(() => {
    const parseFilter = (string) => {
      try {
        const parsed = JSON.parse(string);
        setFilters(parsed);
      } catch (e) {
        setSeverity('error');
        setMessage(t('reports.messages.filterError'));
      }
    };

    const findReport = async () => {
      setReportUsers([]);
      setMentionedUserIds([]);
      setMentionSuggestions([]);
      setSelectedMentionIndex(0);
      const response = await getReport(id);
      if (response.success) {
        const powerbiData = response.data.data.powerbiReport;
        if (onEnabledDownloadPdfChange) {
          onEnabledDownloadPdfChange(powerbiData.enabledDownloadPdf ?? false);
        }
        if (powerbiData.userRelation.configJson) {
          parseFilter(powerbiData.userRelation.configJson);
        }
        setReportId(powerbiData.reportId);
        setUrl(powerbiData.embedUrl);
        setEmbedToken(powerbiData.embedToken);
        setCommentAllowed(powerbiData.commentAllowed);
        setCommentAllowedPages(powerbiData.commentAllowedPages ?? null);
        setReportUsers(powerbiData.users ?? []);
        setLoading(false);
      } else {
        setSeverity('error');
        setMessage(response.data.errorMessage);
        setLoading(false);
      }
    };
    findReport();
  }, [id, onEnabledDownloadPdfChange, t]);

  useEffect(() => {
    if (!commentAllowed) {
      setShowCommentInput(false);
      return;
    }
    const pageAllowed = (
      commentAllowedPages
      && commentAllowedPages?.length
      && commentAllowedPages.includes(currentPageRef.current)
    );
    setShowCommentInput(pageAllowed);
  }, [commentAllowed, commentAllowedPages, pageVersion]);

  useImperativeHandle(ref, () => ({ downloadPDF }), [downloadPDF]);

  const placeholder = () => {
    if (selectedContext === null) {
      return t('reports.placeholders.noSelected');
    }
    return t('reports.placeholders.comment');
  };

  const serializeEditor = () => {
    const editor = commentInputRef.current;
    const mentionedIds = [];

    if (!editor) {
      return { value: '', ids: mentionedIds };
    }

    const isBlockElement = (node) => (
      node.nodeType === Node.ELEMENT_NODE
      && ['DIV', 'P', 'LI'].includes(node.tagName)
    );

    const serializeNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }

      if (node.nodeName === 'BR') return '\n';

      if (
        node.nodeType === Node.ELEMENT_NODE
        && node.dataset.mentionId
        && node.dataset.mentionName
      ) {
        const fullName = node.dataset.mentionName;
        if (node.textContent === fullName) {
          const userId = Number(node.dataset.mentionId);
          if (!mentionedIds.includes(userId)) mentionedIds.push(userId);
          return `@${fullName}`;
        }
        return node.textContent;
      }

      let result = '';
      Array.from(node.childNodes).forEach((child) => {
        if (isBlockElement(child) && result && !result.endsWith('\n')) {
          result += '\n';
        }
        result += serializeNode(child);
      });
      return result;
    };

    const value = serializeNode(editor)
      .replace(/\u00A0/g, ' ');
    return { value: value === '\n' ? '' : value, ids: mentionedIds };
  };

  const normalizeMentionElements = () => {
    const editor = commentInputRef.current;
    if (!editor) return;

    const selection = window.getSelection();

    Array.from(editor.querySelectorAll('[data-mention-id]')).forEach((mentionElement) => {
      const { nextSibling } = mentionElement;
      const hasSeparator = nextSibling
        && nextSibling.nodeType === Node.TEXT_NODE
        && nextSibling.textContent.startsWith('\u00A0');
      if (mentionElement.textContent === mentionElement.dataset.mentionName && hasSeparator) return;

      let caretOffset = null;
      if (selection?.rangeCount && mentionElement.contains(selection.anchorNode)) {
        const preCaretRange = document.createRange();
        preCaretRange.selectNodeContents(mentionElement);
        preCaretRange.setEnd(selection.anchorNode, selection.anchorOffset);
        caretOffset = preCaretRange.toString().length;
      }

      const textNode = document.createTextNode(mentionElement.textContent || '');
      mentionElement.parentNode.replaceChild(textNode, mentionElement);

      if (caretOffset !== null) {
        const range = document.createRange();
        range.setStart(textNode, Math.min(caretOffset, textNode.textContent.length));
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    });
  };

  const getTextPoint = (root, targetOffset) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let remainingOffset = targetOffset;
    let textNode = walker.nextNode();

    while (textNode) {
      const nodeLength = textNode.textContent.length;
      if (remainingOffset <= nodeLength) {
        return { node: textNode, offset: remainingOffset };
      }
      remainingOffset -= nodeLength;
      textNode = walker.nextNode();
    }

    return null;
  };

  const getCurrentMentionRange = () => {
    const editor = commentInputRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || !selection.rangeCount || !editor.contains(selection.anchorNode)) {
      return null;
    }

    const caretRange = selection.getRangeAt(0).cloneRange();
    if (!caretRange.collapsed) return null;

    const textBeforeCaret = document.createRange();
    textBeforeCaret.selectNodeContents(editor);
    textBeforeCaret.setEnd(caretRange.startContainer, caretRange.startOffset);
    const mentionMatch = textBeforeCaret.toString().match(MENTION_PATTERN);
    if (!mentionMatch) return null;

    const mentionStart = mentionMatch.index + mentionMatch[1].length;
    const startPoint = getTextPoint(editor, mentionStart);
    if (!startPoint) return null;

    const mentionRange = document.createRange();
    mentionRange.setStart(startPoint.node, startPoint.offset);
    mentionRange.setEnd(caretRange.startContainer, caretRange.startOffset);
    return mentionRange;
  };

  const restoreCaret = (node, offset) => {
    const editor = commentInputRef.current;
    if (!editor || !editor.contains(node)) return;

    const range = document.createRange();
    range.setStart(node, offset);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const closeMentionSuggestions = () => {
    setMentionSuggestions([]);
    setSelectedMentionIndex(0);
  };

  const handleCommentInput = () => {
    const editor = commentInputRef.current;
    if (!editor) return;

    normalizeMentionElements();
    const { value, ids } = serializeEditor();
    setComment(value);
    setMentionedUserIds(ids);

    const mentionMatch = editor.textContent.match(MENTION_PATTERN);
    if (!mentionMatch) {
      closeMentionSuggestions();
      return;
    }

    const query = mentionMatch[2].toLowerCase();
    const suggestions = reportUsers.filter(({ fullName }) => (
      fullName.toLowerCase().includes(query)
    ));
    setMentionSuggestions(suggestions);
    setSelectedMentionIndex(0);
  };

  const handleCommentPaste = (event) => {
    event.preventDefault();

    const editor = commentInputRef.current;
    const selection = window.getSelection();
    const pastedText = event.clipboardData?.getData('text/plain') || '';
    if (
      !editor
      || !pastedText
      || !selection
      || !selection.rangeCount
      || !editor.contains(selection.anchorNode)
    ) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(pastedText);
    range.insertNode(textNode);
    restoreCaret(textNode, pastedText.length);
    handleCommentInput();
  };

  const selectMention = (user) => {
    const mentionRange = getCurrentMentionRange();
    if (!mentionRange) return;

    mentionRange.deleteContents();

    const mentionElement = document.createElement('span');
    mentionElement.textContent = user.fullName;
    mentionElement.dataset.mentionId = String(user.id);
    mentionElement.dataset.mentionName = user.fullName;
    mentionElement.style.fontWeight = 'bold';
    mentionRange.insertNode(mentionElement);

    const space = document.createTextNode('\u00A0');
    mentionElement.parentNode.insertBefore(space, mentionElement.nextSibling);

    restoreCaret(space, 1);

    const { value, ids } = serializeEditor();
    setComment(value);
    setMentionedUserIds(ids);
    closeMentionSuggestions();
  };

  const handleCommentKeyDown = (event) => {
    if (!mentionSuggestions.length) return;

    if (event.key === 'Tab') {
      event.preventDefault();
      selectMention(mentionSuggestions[selectedMentionIndex]);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedMentionIndex((currentIndex) => (
        Math.min(currentIndex + 1, mentionSuggestions.length - 1)
      ));
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedMentionIndex((currentIndex) => Math.max(currentIndex - 1, 0));
    }

    if (event.key === 'Escape') {
      closeMentionSuggestions();
    }
  };

  const onSaveComment = async () => {
    const body = {
      comment,
      powerbiReportId: id,
      data: selectedContext,
      sheet: currentPageRef.current,
      deadline: deadLineDate,
      taggedUsers: mentionedUserIds,
    };

    const request = await powerbiComment.createPowerbiComment(body);

    if (!request.success) {
      setSeverity('error');
      setMessage(t('powerBiComments.messages.error'));
      return;
    }

    setSeverity('success');
    setMessage(t('powerBiComments.messages.success'));
    setComment('');
    if (commentInputRef.current) commentInputRef.current.innerHTML = '';
    setMentionedUserIds([]);
    setMentionSuggestions([]);
    setSelectedMentionIndex(0);
    setSelectedContext(null);

    const requestRefresh = await powerbiComment.powerbiReportRefresh(id);

    if (!requestRefresh.success) {
      setSeverity('error');
      setMessage(t('powerBiComments.messages.errorUpdatedReport'));
      return;
    }

    try {
      if (!reportRef.current) return;

      await reportRef.current.refresh();
      setSeverity('success');
      setMessage(t('powerBiComments.messages.successUpdatedReport'));
    } catch (error) {
      setSeverity('error');
      setMessage(t('powerBiComments.messages.errorRefresh'));
    }
  };

  const commentInput = () => (
    <FormControl fullWidth margin="normal" className={styles.form}>
      <Box display="flex" flexDirection="row" className={styles.row}>
        <Box flex={1}>
          <DatePicker
            minDate={new Date()}
            selected={deadLineDate}
            onChange={(newDate) => setDeadLineDate(newDate)}
            dateFormat="dd-MM-yyyy"
            locale={shortLocale}
            wrapperClassName={styles.datePickerWrapper}
            customInput={
              <TextField
                label={t('powerBiComments.form.deadline')}
                variant="outlined"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            }
          />
        </Box>
        <Box flex={4} className={styles.commentEditorWrapper}>
          <Typography className={styles.commentLabel}>
            {t('powerBiComments.form.comment')}
          </Typography>
          <Box
            className={styles.commentEditor}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label={t('powerBiComments.form.comment')}
            data-placeholder={placeholder()}
            ref={commentInputRef}
            onInput={handleCommentInput}
            onKeyDown={handleCommentKeyDown}
            onPaste={handleCommentPaste}
          />
          <Popper
            open={mentionSuggestions.length > 0}
            anchorEl={commentInputRef.current}
            placement="top-start"
            modifiers={{ flip: { enabled: false } }}
            disablePortal
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={closeMentionSuggestions}>
              <Paper style={{ width: 150, maxHeight: 300, overflowY: 'auto' }}>
                <MenuList dense>
                  {mentionSuggestions.map((user, index) => (
                    <MenuItem
                      key={user.id}
                      selected={index === selectedMentionIndex}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectMention(user)}
                    >
                      <span
                        style={{
                          display: 'block',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.fullName}
                      </span>
                    </MenuItem>
                  ))}
                </MenuList>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </Box>

        <Box flex={1}>
          <Button
            variant="outlined"
            disabled={selectedContext === null || comment === ''}
            onClick={onSaveComment}
            className={styles.button}
          >
            {t('powerBiComments.form.submit')}
          </Button>
        </Box>
      </Box>
    </FormControl>
  );

  const conditionalRender = () => {
    if (loading) {
      return (
        <div className={styles.container}>
          <CircularProgress className={styles.loading} />
        </div>
      );
    }
    return (
      <PowerBIEmbed
        embedConfig={{
          type: 'report', // Supported types: report, dashboard, tile, visual and qna
          id: reportId,
          embedUrl: url,
          accessToken: embedToken,
          tokenType: models.TokenType.Embed,
          filters: Array.isArray(filters) ? filters : [filters].filter(Boolean),
          settings: {
            panes: {
              filters: {
                expanded: true,
                visible: false,
              },
            },
          },
        }}
        getEmbeddedComponent={(embeddedReport) => {
          reportRef.current = embeddedReport;
        }}
        eventHandlers={
          new Map([
            ['loaded', () => {
              if (typeof onReportLoaded === 'function') {
                onReportLoaded(true);
              }
            }],
            ['pageChanged', (event) => {
              if (event.detail?.newPage?.displayName) {
                setSelectedContext(null);
                updatePage(event.detail.newPage.displayName);
              }
            }],
            ['dataSelected', (event) => {
              const { dataPoints } = event.detail;
              if (!dataPoints || dataPoints.length === 0) {
                setSelectedContext(null);
                return;
              }
              const dp = dataPoints[0];
              setSelectedContext({
                values: dp.identity.map((i) => ({
                  table: i.target.table,
                  column: i.target.column,
                  value: i.equals,
                })),
              });
            }],
          ])
        }
        cssClassName={styles.report}
      />
    );
  };
  return (
    <div>
      <div
        className={clsx({
          [styles.root]: !fullScreen,
          [styles.rootFullScreen]: fullScreen,
        })}
      >
        {conditionalRender()}
        {showCommentInput && commentInput()}
        <Snackbar
          open={message !== ''}
          message={message}
          severity={severity}
          onClose={() => setMessage('')}
        />
      </div>
    </div>
  );
});

Report.displayName = 'Report';

export default Report;
